import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Users, Store, Package, Lightbulb, Eye, MousePointer, Heart,
  CheckCircle2, Loader2, ShieldAlert, Trash2, RefreshCw, ArrowLeft,
  Clock, UserPlus, Bug, Sparkles, FileText, Database, FlaskConical, Mail
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import TestProductsManager from '../components/TestProductsManager';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEEDBACK_ICONS = {
  bug: Bug,
  idea: Sparkles,
  miglioramento: RefreshCw,
  altro: FileText,
};

const STATUS_LABELS = {
  new: { label: 'Nuovo', color: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'In lavorazione', color: 'bg-amber-100 text-amber-700' },
  resolved: { label: 'Risolto', color: 'bg-green-100 text-green-700' },
  archived: { label: 'Archiviato', color: 'bg-slate-100 text-slate-600' },
};

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch {
    return iso;
  }
}

function StatCard({ icon: Icon, label, value, sublabel, color = 'blue', testId }) {
  const colors = {
    blue: 'from-blue-500 to-blue-600 text-white',
    green: 'from-green-500 to-emerald-600 text-white',
    amber: 'from-amber-500 to-orange-600 text-white',
    purple: 'from-purple-500 to-pink-600 text-white',
    slate: 'from-slate-700 to-slate-900 text-white',
  };
  return (
    <div className={`rounded-2xl p-5 bg-gradient-to-br ${colors[color]} shadow-md`} data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide opacity-80">{label}</p>
          <p className="text-3xl font-bold mt-1 font-outfit">{value}</p>
          {sublabel && <p className="text-xs opacity-90 mt-1">{sublabel}</p>}
        </div>
        <div className="p-2 bg-white/20 rounded-lg">
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading, isAuthenticated } = useAuth();

  const [stats, setStats] = useState(null);
  const [feedbacks, setFeedbacks] = useState([]);
  const [users, setUsers] = useState([]);
  const [shops, setShops] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [testDataCount, setTestDataCount] = useState({ shops: 0, products: 0 });
  const [testDataLoading, setTestDataLoading] = useState(false);
  const [digestLoading, setDigestLoading] = useState(false);

  const handleSendDigest = async () => {
    if (!window.confirm("Inviare ora l'email di riepilogo a kyloo.app@libero.it?")) return;
    setDigestLoading(true);
    try {
      const res = await axios.post(`${API}/admin/digest/send-now`, {}, { withCredentials: true });
      if (res.data.sent) {
        toast.success(`Digest inviato: ${res.data.feedbacks} feedback, ${res.data.new_shops} nuovi negozi`);
      } else {
        toast.warning('Nessuna novità da segnalare nelle ultime 24h');
      }
    } catch (e) {
      toast.error('Errore invio digest');
      console.error(e);
    } finally {
      setDigestLoading(false);
    }
  };

  const fetchTestDataCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/test-data/count`, { withCredentials: true });
      setTestDataCount(res.data);
    } catch (e) {
      console.error('Test data count fetch failed:', e);
    }
  }, []);

  const handleSeedTestData = async () => {
    if (!window.confirm('Creare 14 negozi + 150 prodotti di test vicino a Conselve? (Verranno prima rimossi eventuali dati di test esistenti)')) return;
    setTestDataLoading(true);
    try {
      const res = await axios.post(`${API}/admin/test-data/seed`, {}, { withCredentials: true });
      toast.success(`Creati ${res.data.shops_created} negozi e ${res.data.products_created} prodotti di test`);
      await fetchTestDataCount();
      await fetchAll();
    } catch (e) {
      toast.error('Errore durante la creazione dei dati di test');
      console.error(e);
    } finally {
      setTestDataLoading(false);
    }
  };

  const handleDeleteTestData = async () => {
    if (!window.confirm(`Disattivare ${testDataCount.products} prodotti e ${testDataCount.shops} negozi di test? Questa azione è irreversibile.`)) return;
    setTestDataLoading(true);
    try {
      const res = await axios.delete(`${API}/admin/test-data`, { withCredentials: true });
      toast.success(`Rimossi ${res.data.products_deleted} prodotti e ${res.data.shops_deleted} negozi di test`);
      await fetchTestDataCount();
      await fetchAll();
    } catch (e) {
      toast.error('Errore durante la disattivazione dei dati di test');
      console.error(e);
    } finally {
      setTestDataLoading(false);
    }
  };

  const fetchAll = useCallback(async () => {
    setRefreshing(true);
    try {
      const [s, f, u, sh, a] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/feedbacks`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/shops`, { withCredentials: true }),
        axios.get(`${API}/admin/activities`, { withCredentials: true }),
      ]);
      setStats(s.data);
      // v1.8.3 — admin list endpoints now paginate: {items, total, limit, skip}.
      // Preserve legacy handling if a plain array is returned.
      setFeedbacks(Array.isArray(f.data) ? f.data : (f.data?.items || []));
      setUsers(Array.isArray(u.data) ? u.data : (u.data?.items || []));
      setShops(Array.isArray(sh.data) ? sh.data : (sh.data?.items || []));
      setActivities(a.data);
    } catch (e) {
      console.error('Admin fetch error:', e);
      toast.error('Errore nel caricamento dati admin');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !user?.is_admin) {
      navigate('/home');
      return;
    }
    fetchAll();
    fetchTestDataCount();
  }, [authLoading, isAuthenticated, user, navigate, fetchAll, fetchTestDataCount]);

  const handleStatusChange = async (id, status) => {
    try {
      await axios.put(`${API}/admin/feedbacks/${id}`, { status }, { withCredentials: true });
      setFeedbacks((prev) => prev.map((f) => (f.id === id ? { ...f, status } : f)));
      toast.success('Stato aggiornato');
    } catch {
      toast.error('Errore aggiornamento');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo suggerimento?')) return;
    try {
      await axios.delete(`${API}/admin/feedbacks/${id}`, { withCredentials: true });
      setFeedbacks((prev) => prev.filter((f) => f.id !== id));
      toast.success('Eliminato');
    } catch {
      toast.error('Errore eliminazione');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!isAuthenticated || !user?.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="text-center">
          <ShieldAlert className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h2 className="text-xl font-bold text-slate-800">Accesso negato</h2>
          <p className="text-slate-600 mt-2">Quest'area è riservata agli amministratori.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-12" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900 text-white px-4 py-5 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/home')}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/10"
              data-testid="admin-back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Home
            </Button>
            <div>
              <h1 className="text-2xl font-outfit font-bold flex items-center gap-2">
                <ShieldAlert className="w-6 h-6 text-amber-400" />
                Admin Dashboard
              </h1>
              <p className="text-xs text-slate-300 mt-1">{user.email}</p>
            </div>
          </div>
          <Button
            onClick={fetchAll}
            disabled={refreshing}
            size="sm"
            className="bg-white/10 hover:bg-white/20 text-white border-white/20"
            data-testid="admin-refresh-button"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-stats-grid">
            <StatCard
              icon={Users}
              label="Utenti totali"
              value={stats.users.total}
              sublabel={`+${stats.users.new_7d} ultimi 7gg`}
              color="blue"
              testId="stat-users"
            />
            <StatCard
              icon={Store}
              label="Negozi"
              value={stats.shops.total}
              sublabel={`${stats.shops.verified} verificati`}
              color="green"
              testId="stat-shops"
            />
            <StatCard
              icon={Package}
              label="Prodotti"
              value={stats.products.total}
              sublabel={`${stats.products.total_views} viste`}
              color="purple"
              testId="stat-products"
            />
            <StatCard
              icon={Lightbulb}
              label="Suggerimenti"
              value={stats.feedbacks.total}
              sublabel={`${stats.feedbacks.new} nuovi`}
              color="amber"
              testId="stat-feedbacks"
            />
            <StatCard
              icon={UserPlus}
              label="Iscrizioni 30gg"
              value={stats.users.new_30d}
              sublabel="utenti registrati"
              color="blue"
              testId="stat-signups"
            />
            <StatCard
              icon={Store}
              label="Nuovi negozi 30gg"
              value={stats.shops.new_30d}
              sublabel="registrazioni"
              color="green"
              testId="stat-new-shops"
            />
            <StatCard
              icon={MousePointer}
              label="Click negozi"
              value={stats.products.total_clicks}
              sublabel="totali"
              color="purple"
              testId="stat-clicks"
            />
            <StatCard
              icon={Heart}
              label="Preferiti"
              value={stats.favorites.total}
              sublabel="totali"
              color="slate"
              testId="stat-favorites"
            />
          </div>
        )}

        {/* Test Data Management Panel */}
        <div
          className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-5 shadow-sm"
          data-testid="test-data-panel"
        >
          <div className="flex items-start gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
              <FlaskConical className="w-5 h-5 text-amber-700" />
            </div>
            <div className="flex-1">
              <h3 className="font-outfit font-bold text-slate-800 text-lg">Dati di test</h3>
              <p className="text-sm text-slate-600">
                Genera o disattiva 150 prodotti demo (ferramenta, cosmetici, servizi) in 14 negozi nei dintorni di <strong>Conselve</strong> (50&nbsp;km).
                {testDataCount.products > 0 && (
                  <span className="block text-amber-700 font-medium mt-1">
                    Attualmente attivi: <strong data-testid="test-products-count">{testDataCount.products}</strong> prodotti su <strong data-testid="test-shops-count">{testDataCount.shops}</strong> negozi.
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={handleSeedTestData}
              disabled={testDataLoading}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="seed-test-data-button"
            >
              {testDataLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Database className="w-4 h-4 mr-2" />
              )}
              {testDataCount.products > 0 ? 'Ricrea dati di test' : 'Crea dati di test'}
            </Button>
            <Button
              onClick={handleDeleteTestData}
              disabled={testDataLoading || testDataCount.products === 0}
              size="sm"
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-50"
              data-testid="delete-test-data-button"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Disattiva prodotti di test
            </Button>
            <Button
              onClick={handleSendDigest}
              disabled={digestLoading}
              size="sm"
              variant="outline"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="send-digest-now-button"
            >
              {digestLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Mail className="w-4 h-4 mr-2" />
              )}
              Invia digest ora
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="feedbacks" className="w-full">
          <TabsList className="grid w-full grid-cols-5 bg-white shadow-sm">
            <TabsTrigger value="feedbacks" data-testid="tab-feedbacks">
              Suggerimenti {stats?.feedbacks.new > 0 && (
                <Badge className="ml-2 bg-red-500 text-white text-xs">{stats.feedbacks.new}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="activities" data-testid="tab-activities">Attività</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">Utenti</TabsTrigger>
            <TabsTrigger value="shops" data-testid="tab-shops">Negozi</TabsTrigger>
            <TabsTrigger value="test-products" data-testid="tab-test-products">
              Prodotti Test {testDataCount.products > 0 && (
                <Badge className="ml-2 bg-amber-500 text-white text-xs">{testDataCount.products}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Feedbacks Tab */}
          <TabsContent value="feedbacks">
            <div className="bg-white rounded-2xl shadow-sm divide-y" data-testid="admin-feedbacks-list">
              {feedbacks.length === 0 && (
                <p className="p-6 text-center text-slate-500">Nessun suggerimento ricevuto.</p>
              )}
              {feedbacks.map((f) => {
                const Icon = FEEDBACK_ICONS[f.type] || FileText;
                const status = STATUS_LABELS[f.status] || STATUS_LABELS.new;
                return (
                  <div key={f.id} className="p-4 flex gap-3" data-testid={`feedback-${f.id}`}>
                    <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-slate-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-800 capitalize">{f.type}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${status.color}`}>
                          {status.label}
                        </span>
                        <span className="text-xs text-slate-500 ml-auto">
                          {formatDate(f.created_at)}
                        </span>
                      </div>
                      <p className="text-sm text-slate-700 mt-1 break-words">{f.message}</p>
                      {f.user_email && (
                        <p className="text-xs text-slate-500 mt-1">da: {f.user_email}</p>
                      )}
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {f.status !== 'in_progress' && (
                          <Button size="sm" variant="outline" onClick={() => handleStatusChange(f.id, 'in_progress')}
                            data-testid={`feedback-progress-${f.id}`}>
                            <Clock className="w-3 h-3 mr-1" /> In lavorazione
                          </Button>
                        )}
                        {f.status !== 'resolved' && (
                          <Button size="sm" variant="outline" onClick={() => handleStatusChange(f.id, 'resolved')}
                            data-testid={`feedback-resolve-${f.id}`}>
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Risolvi
                          </Button>
                        )}
                        {f.status !== 'archived' && (
                          <Button size="sm" variant="outline" onClick={() => handleStatusChange(f.id, 'archived')}>
                            Archivia
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50"
                          onClick={() => handleDelete(f.id)} data-testid={`feedback-delete-${f.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* Activities Tab */}
          <TabsContent value="activities">
            <div className="bg-white rounded-2xl shadow-sm divide-y" data-testid="admin-activities-list">
              {activities.length === 0 && (
                <p className="p-6 text-center text-slate-500">Nessuna attività recente.</p>
              )}
              {activities.map((a, idx) => (
                <div key={`${a.type}-${a.created_at}-${idx}`} className="p-4 flex gap-3 items-start">
                  <div className="w-2 h-2 rounded-full bg-blue-500 mt-2 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-800">{a.title}</p>
                    <p className="text-xs text-slate-600 mt-0.5 break-words">{a.description}</p>
                  </div>
                  <span className="text-xs text-slate-500 flex-shrink-0">
                    {formatDate(a.created_at)}
                  </span>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="bg-white rounded-2xl shadow-sm divide-y" data-testid="admin-users-list">
              {users.length === 0 && (
                <p className="p-6 text-center text-slate-500">Nessun utente registrato.</p>
              )}
              {users.map((u) => (
                <div key={u.user_id} className="p-4 flex items-center gap-3">
                  {u.picture ? (
                    <img src={u.picture} alt="" className="w-10 h-10 rounded-full" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                      <Users className="w-5 h-5 text-slate-500" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-slate-800 truncate">{u.name}</span>
                      {u.is_admin && (
                        <Badge className="bg-amber-100 text-amber-700 text-xs">Admin</Badge>
                      )}
                      {u.shop && (
                        <Badge className="bg-green-100 text-green-700 text-xs">
                          {u.shop.email_verified ? 'Negoziante verificato' : 'Negoziante'}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 truncate">{u.email}</p>
                    {u.shop && (
                      <p className="text-xs text-slate-600 mt-0.5">Negozio: {u.shop.nome}</p>
                    )}
                  </div>
                  <span className="text-xs text-slate-500 flex-shrink-0">
                    {formatDate(u.created_at)}
                  </span>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Shops Tab */}
          <TabsContent value="shops">
            <div className="bg-white rounded-2xl shadow-sm divide-y" data-testid="admin-shops-list">
              {shops.length === 0 && (
                <p className="p-6 text-center text-slate-500">Nessun negozio registrato.</p>
              )}
              {shops.map((s) => (
                <div key={s.id} className="p-4 flex items-center gap-3">
                  {s.logo ? (
                    <img src={s.logo} alt="" className="w-10 h-10 rounded-lg object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-slate-200 flex items-center justify-center">
                      <Store className="w-5 h-5 text-slate-500" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-slate-800">{s.nome}</span>
                      {s.email_verified && (
                        <Badge className="bg-green-100 text-green-700 text-xs">
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Verificato
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-500">{s.indirizzo}, {s.citta}</p>
                    <p className="text-xs text-slate-600 mt-0.5">
                      P.IVA: {s.partita_iva} · {s.product_count} prodotti
                    </p>
                  </div>
                  <span className="text-xs text-slate-500 flex-shrink-0">
                    {formatDate(s.created_at)}
                  </span>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Test Products Tab */}
          <TabsContent value="test-products">
            <TestProductsManager onChange={fetchTestDataCount} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
